import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";

import moment from "moment";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  ratesRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
    padding: theme.spacing(2),
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
}));

// Valor = chave de tradução (resolvida no render, para acompanhar a troca de idioma).
const CATEGORY_LABELS = {
  MARKETING: "templateCreditManager.categories.MARKETING",
  UTILITY: "templateCreditManager.categories.UTILITY",
  AUTHENTICATION: "templateCreditManager.categories.AUTHENTICATION",
};

const TX_TYPES = {
  purchase: { labelKey: "templateCreditManager.txTypes.purchase", color: "#4caf50" },
  manual_adjust: { labelKey: "templateCreditManager.txTypes.manual_adjust", color: "#2196f3" },
  debit: { labelKey: "templateCreditManager.txTypes.debit", color: "#f44336" },
  refund: { labelKey: "templateCreditManager.txTypes.refund", color: "#ff9800" },
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

export default function TemplateCreditManager() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [rates, setRates] = useState({
    MARKETING: "0",
    UTILITY: "0",
    AUTHENTICATION: "0",
  });
  const [savingRates, setSavingRates] = useState(false);

  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);

  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/template-credits/admin/accounts");
      setCompanies(data.companies || []);
      if (data.rates) {
        setRates({
          MARKETING: String(data.rates.MARKETING ?? 0),
          UTILITY: String(data.rates.UTILITY ?? 0),
          AUTHENTICATION: String(data.rates.AUTHENTICATION ?? 0),
        });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleSaveRates = async () => {
    setSavingRates(true);
    try {
      await api.put("/template-credits/admin/rates", {
        rates: Object.keys(CATEGORY_LABELS).map((category) => ({
          category,
          price: Number(rates[category]) || 0,
        })),
      });
      toast.success(i18n.t("templateCreditManager.toasts.ratesSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingRates(false);
    }
  };

  const handleToggle = async (company) => {
    try {
      const { data } = await api.put(
        `/template-credits/admin/accounts/${company.id}`,
        { enabled: !company.enabled }
      );
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id
            ? { ...c, enabled: data.enabled, balance: data.balance }
            : c
        )
      );
    } catch (err) {
      toastError(err);
    }
  };

  const handleAdjust = async () => {
    if (!adjustTarget) return;
    setAdjusting(true);
    try {
      const { data } = await api.post(
        `/template-credits/admin/accounts/${adjustTarget.id}/adjust`,
        { delta: Number(adjustDelta), reason: adjustReason }
      );
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === adjustTarget.id ? { ...c, balance: data.balance } : c
        )
      );
      toast.success(i18n.t("templateCreditManager.toasts.balanceAdjusted"));
      setAdjustTarget(null);
      setAdjustDelta("");
      setAdjustReason("");
    } catch (err) {
      toastError(err);
    } finally {
      setAdjusting(false);
    }
  };

  const openHistory = async (company) => {
    setHistoryTarget(company);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(
        `/template-credits/admin/accounts/${company.id}/transactions`
      );
      setHistoryRows(data.transactions || []);
    } catch (err) {
      toastError(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6">{i18n.t("templateCreditManager.rates.title")}</Typography>
          {loading && <CircularProgress size={22} />}
        </Box>
        <Paper variant="outlined" className={classes.ratesRow}>
          {Object.keys(CATEGORY_LABELS).map((category) => (
            <TextField
              key={category}
              size="small"
              variant="outlined"
              type="number"
              inputProps={{ min: 0, step: 0.0001 }}
              label={i18n.t("templateCreditManager.rates.fieldLabel", {
                category: i18n.t(CATEGORY_LABELS[category]),
              })}
              value={rates[category]}
              onChange={(e) =>
                setRates((prev) => ({ ...prev, [category]: e.target.value }))
              }
              style={{ minWidth: 210 }}
            />
          ))}
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={savingRates}
            onClick={handleSaveRates}
          >
            {savingRates
              ? i18n.t("common.saving")
              : i18n.t("templateCreditManager.rates.save")}
          </Button>
        </Paper>
        <Typography variant="caption" color="textSecondary">
          {i18n.t("templateCreditManager.rates.hint")}
        </Typography>
      </div>

      <div className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6">
            {i18n.t("templateCreditManager.companies.title")}
          </Typography>
          <Button size="small" variant="outlined" onClick={loadAccounts}>
            {i18n.t("common.refresh")}
          </Button>
        </Box>
        <Paper variant="outlined" className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("common.company")}</TableCell>
                <TableCell align="center">
                  {i18n.t("templateCreditManager.companies.billingEnabled")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("templateCreditManager.companies.balance")}
                </TableCell>
                <TableCell align="center">{i18n.t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id}>
                  <TableCell>
                    {company.name}
                    {!company.status && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        {i18n.t("templateCreditManager.companies.companyDisabled")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      color="primary"
                      checked={company.enabled}
                      onChange={() => handleToggle(company)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {company.enabled || company.balance > 0
                      ? formatCurrency(company.balance)
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      onClick={() => setAdjustTarget(company)}
                    >
                      {i18n.t("templateCreditManager.companies.adjustBalance")}
                    </Button>
                    <Button size="small" onClick={() => openHistory(company)}>
                      {i18n.t("templateCreditManager.companies.statement")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && companies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("templateCreditManager.companies.empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </div>

      <Dialog
        open={!!adjustTarget}
        onClose={() => setAdjustTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("templateCreditManager.adjustDialog.title", {
            name: adjustTarget?.name || "",
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {i18n.t("templateCreditManager.adjustDialog.hint", {
              balance: formatCurrency(adjustTarget?.balance),
            })}
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="number"
            label={i18n.t("templateCreditManager.adjustDialog.amount")}
            value={adjustDelta}
            onChange={(e) => setAdjustDelta(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={i18n.t("templateCreditManager.adjustDialog.reason")}
            value={adjustReason}
            onChange={(e) => setAdjustReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustTarget(null)}>
            {i18n.t("common.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={
              adjusting || !Number(adjustDelta) || !adjustReason.trim()
            }
            onClick={handleAdjust}
          >
            {adjusting
              ? i18n.t("templateCreditManager.adjustDialog.adjusting")
              : i18n.t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("templateCreditManager.historyDialog.title", {
            name: historyTarget?.name || "",
          })}
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("common.date")}</TableCell>
                  <TableCell>{i18n.t("common.type")}</TableCell>
                  <TableCell>
                    {i18n.t("templateCreditManager.historyDialog.category")}
                  </TableCell>
                  <TableCell>
                    {i18n.t("templateCreditManager.historyDialog.template")}
                  </TableCell>
                  <TableCell>
                    {i18n.t("templateCreditManager.historyDialog.source")}
                  </TableCell>
                  <TableCell align="right">
                    {i18n.t("templateCreditManager.historyDialog.amount")}
                  </TableCell>
                  <TableCell align="right">
                    {i18n.t("templateCreditManager.historyDialog.balanceAfter")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.map((tx) => {
                  const txType = TX_TYPES[tx.type];
                  const info = txType
                    ? { label: i18n.t(txType.labelKey), color: txType.color }
                    : { label: tx.type, color: "#9e9e9e" };
                  return (
                    <TableRow key={tx.id}>
                      <TableCell>
                        {moment(tx.createdAt).format("DD/MM/YY HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={info.label}
                          style={{ backgroundColor: info.color, color: "#fff" }}
                        />
                      </TableCell>
                      <TableCell>{tx.category || "-"}</TableCell>
                      <TableCell>{tx.templateName || "-"}</TableCell>
                      <TableCell>{tx.source || "-"}</TableCell>
                      <TableCell
                        align="right"
                        style={{
                          color: Number(tx.amount) < 0 ? "#f44336" : "#4caf50",
                        }}
                      >
                        {formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(tx.balanceAfter)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {historyRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("templateCreditManager.historyDialog.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryTarget(null)}>
            {i18n.t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
